// src/components/patient/assistant/AssistantCards.jsx
//
// Renders ONLY the allow-listed card fields the API returns (Patient Task 4).
// No raw tool calls, provider payloads, prompts or identifiers are shown; a
// card's buttons are exactly the server's `actions`. Buttons that change
// something open an application dialog first (never a browser dialog).

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { describeCancellationPolicy } from "../../../lib/cancellationPolicy";
import { formatDateTimeInZone, formatLocalDate, formatTimeInZone } from "../../../lib/displayTimezone";
import { Pill, money } from "../booking/bookingUi";

export function secondsLeft(expiresAt, offsetMs) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - (Date.now() + offsetMs)) / 1000));
}

export function HoldCountdown({ expiresAt, serverNow, onExpire }) {
  const offset = useRef(serverNow ? new Date(serverNow).getTime() - Date.now() : 0);
  const [left, setLeft] = useState(() => secondsLeft(expiresAt, offset.current));
  const expired = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      const next = secondsLeft(expiresAt, offset.current);
      setLeft(next);
      if (next <= 0 && !expired.current) {
        expired.current = true;
        onExpire?.();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpire]);

  const mins = `${Math.floor(left / 60)}`.padStart(2, "0");
  const secs = `${left % 60}`.padStart(2, "0");
  return (
    <p className="text-sm font-semibold" aria-live="polite" data-testid="assistant-countdown">
      {left > 0 ? `Held for ${mins}:${secs}` : "This hold has ended."}
    </p>
  );
}

function When({ card }) {
  if (!card.start_at) return null;
  const zone = card.timezone;
  return (
    <p className="text-sm text-slate-700">
      {formatDateTimeInZone(card.start_at, zone)}
      {card.end_at ? ` – ${formatTimeInZone(card.end_at, zone)}` : ""}
      {zone ? <span className="ml-1 text-xs text-slate-500">({zone})</span> : null}
    </p>
  );
}

function Buttons({ actions, disabled, onAction, card }) {
  if (!actions?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {actions.map((button) => (
        <button
          key={`${button.action}-${button.ref || ""}`}
          type="button"
          disabled={disabled}
          onClick={() => onAction(button, card)}
          className={
            button.requires_confirmation
              ? "rounded-2xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              : "rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
          }
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

// Same allowlist as the API schema: internal patient routes only.
const INTERNAL_LINK = /^\/patient\/(appointments(\/[0-9a-fA-F-]{36}(#(forms|payment|video))?)?|book-appointment)$/;
export function safeLink(link) {
  return typeof link === "string" && INTERNAL_LINK.test(link) ? link : null;
}

// Cancellation policy in the same wording as the booking page (held version
// at review, pinned snapshot on an appointment). Never promises a refund.
function PolicyBlock({ card }) {
  if (!card.policy) return null;
  const view = describeCancellationPolicy(card.policy, { currency: card.currency, startAt: card.start_at, timeZone: card.timezone });
  return (
    <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2" data-testid="assistant-policy">
      <p className="text-xs font-semibold text-slate-700">Cancellation policy: {view.headline}</p>
      {view.details.map((line) => (
        <p key={line} className="text-xs text-slate-600">{line}</p>
      ))}
    </div>
  );
}

const NOTICE_TONES = {
  emergency: "border-rose-300 bg-rose-50",
  unsupported_track: "border-amber-300 bg-amber-50",
  location_needed: "border-sky-200 bg-sky-50",
  clarify: "border-sky-200 bg-sky-50",
  no_results: "border-slate-200 bg-white",
  out_of_scope: "border-slate-200 bg-white",
};

function NoticeCard({ card, disabled, onChoice, onUseLocation, onEditFilters, locating }) {
  return (
    <article
      className={`rounded-2xl border p-4 ${NOTICE_TONES[card.notice_kind] || NOTICE_TONES.no_results}`}
      role={card.notice_kind === "emergency" ? "alert" : undefined}
      data-testid={`assistant-notice-${card.notice_kind}`}
    >
      <h3 className="text-sm font-semibold">{card.title}</h3>
      {card.message ? <p className="mt-1 text-sm text-slate-700">{card.message}</p> : null}
      {card.notice_kind === "location_needed" && (onUseLocation || onEditFilters) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {onUseLocation ? (
            <button type="button" disabled={disabled || locating} onClick={onUseLocation}
              className="rounded-full bg-violet-600 px-3 py-1 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
              {locating ? "Locating…" : "Use my location"}
            </button>
          ) : null}
          {onEditFilters ? (
            <button type="button" disabled={disabled} onClick={onEditFilters}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
              Choose a city
            </button>
          ) : null}
        </div>
      ) : null}
      {card.choices?.length ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {card.choices.map((choice) => (
            <button key={choice} type="button" disabled={disabled || !onChoice} onClick={() => onChoice?.(choice)}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
              {choice}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function AssistantCard({ card, disabled, onAction, onPay, payingLink, onHoldExpired, onChoice, onUseLocation, onEditFilters, locating }) {
  const base = "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm";
  const title = card.title || card.service_name;
  const people = [card.doctor_name, card.clinic_name].filter(Boolean).join(" · ");

  if (card.type === "notice") {
    return <NoticeCard card={card} disabled={disabled} onChoice={onChoice} onUseLocation={onUseLocation} onEditFilters={onEditFilters} locating={locating} />;
  }

  if (card.type === "service" || card.type === "reschedule_options") {
    return (
      <article className={base} data-testid={`assistant-card-${card.type}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          <div className="flex flex-wrap gap-1">
            {card.recommendation === "exploratory" ? <Pill tone="warn">Early match</Pill> : null}
            {card.recommendation === "matched" ? <Pill tone="success">Matches your date &amp; place</Pill> : null}
            {card.entity_label ? <Pill tone="violet">{card.entity_label}</Pill> : null}
          </div>
        </div>
        {people ? <p className="text-xs text-slate-600">{people}</p> : null}
        {card.specialization ? <p className="text-xs text-slate-500">{card.specialization}</p> : null}
        <div className="mt-2 flex flex-wrap gap-2">
          {card.price !== null && card.price !== undefined ? <Pill tone="neutral">{money(card.price, card.currency)}</Pill> : null}
          {card.duration_min ? <Pill tone="neutral">{card.duration_min} min</Pill> : null}
          {card.timezone ? <Pill tone="info">Times in {card.timezone}</Pill> : null}
          {card.before_forms_count ? <Pill tone="neutral">{card.before_forms_count} pre-visit form{card.before_forms_count === 1 ? "" : "s"}</Pill> : null}
        </div>
        {card.payment_message ? <p className="mt-2 text-xs text-slate-600">{card.payment_message}</p> : null}
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={`Available times for ${title}`}>
          {(card.slots || []).map((slot) => (
            <li key={slot.ref}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onAction(slot.actions[0], { ...card, ...slot, type: "slot" })}
                className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-left text-sm font-semibold hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-50"
              >
                <span className="block">{formatLocalDate(slot.local_date)} · {formatTimeInZone(slot.start_at, slot.timezone)}</span>
                <span className="block text-xs font-normal text-slate-500">{slot.mode_label}{slot.location_name ? ` · ${slot.location_name}` : ""}</span>
              </button>
            </li>
          ))}
        </ul>
      </article>
    );
  }

  if (card.type === "hold" || card.type === "reschedule_hold") {
    return (
      <article className={`${base} border-sky-300`} data-testid={`assistant-card-${card.type}`}>
        <h3 className="text-sm font-semibold">{card.type === "hold" ? "Time held for you" : "New time held"}: {title}</h3>
        {people ? <p className="text-xs text-slate-600">{people}</p> : null}
        <When card={card} />
        {card.mode_label || card.location_name ? (
          <p className="text-xs text-slate-600">{[card.mode_label, card.location_name].filter(Boolean).join(" · ")}{card.duration_min ? ` · ${card.duration_min} min` : ""}</p>
        ) : null}
        {card.price !== null && card.price !== undefined ? <p className="text-sm font-semibold">{money(card.price, card.currency)}</p> : null}
        {card.payment_message ? <p className="text-xs text-slate-600">{card.payment_message}</p> : null}
        <PolicyBlock card={card} />
        {card.before_forms_count ? (
          <p className="mt-2 text-xs text-slate-600">
            This service has {card.before_forms_count} pre-visit form{card.before_forms_count === 1 ? "" : "s"}. You can fill {card.before_forms_count === 1 ? "it" : "them"} from the appointment after booking.
          </p>
        ) : null}
        <HoldCountdown expiresAt={card.expires_at} serverNow={card.server_now} onExpire={onHoldExpired} />
        <Buttons actions={card.actions} disabled={disabled} onAction={onAction} card={card} />
      </article>
    );
  }

  if (card.type === "booking_result") {
    const required = card.payment_requirement === "required";
    return (
      <article className={`${base} ${required ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="assistant-card-booking_result">
        <h3 className="text-sm font-semibold">{card.title}</h3>
        <p className="text-sm">{card.service_name} with {card.doctor_name}</p>
        <When card={card} />
        {card.appointment_number ? <p className="text-xs">Reference {card.appointment_number}</p> : null}
        {card.payment_message ? <p className="mt-1 text-xs font-semibold">{card.payment_message}</p> : null}
        {card.delivery_message ? <p className="mt-1 text-xs text-slate-600">{card.delivery_message}</p> : null}
        <div className="mt-3 flex flex-wrap gap-2">
          {required && card.payment_appointment_link ? (
            <button type="button" onClick={() => onPay(card)} disabled={payingLink === card.payment_appointment_link} aria-busy={payingLink === card.payment_appointment_link || undefined}
              className="rounded-2xl bg-amber-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {payingLink === card.payment_appointment_link ? "Opening payment…" : "Pay now"}
            </button>
          ) : null}
          {card.next_steps?.length
            ? card.next_steps.filter((step) => safeLink(step.link)).map((step) => (
                <Link key={step.link} to={step.link} className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold">{step.label}</Link>
              ))
            : safeLink(card.link) ? <Link to={card.link} className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold">View appointment</Link> : null}
        </div>
      </article>
    );
  }

  return (
    <article className={base} data-testid={`assistant-card-${card.type}`}>
      <h3 className="text-sm font-semibold">{title}</h3>
      {people ? <p className="text-xs text-slate-600">{people}</p> : null}
      <When card={card} />
      {card.status_label ? <p className="text-xs text-slate-600">{card.status_label}{card.payment_message ? ` · ${card.payment_message}` : ""}</p> : null}
      {card.policy ? <PolicyBlock card={card} /> : null}
      {card.late_fee_review ? <p className="mt-1 text-xs font-semibold text-amber-800">You are inside the policy window; any fee is reviewed by the clinic.</p> : null}
      {card.financial_message ? <p className="mt-1 text-xs text-slate-700">{card.financial_message}</p> : null}
      {card.unavailable && Object.values(card.unavailable).some(Boolean) && !card.actions?.length ? (
        <p className="mt-1 text-xs text-slate-600">{Object.values(card.unavailable).filter(Boolean)[0]}</p>
      ) : null}
      <Buttons actions={card.actions} disabled={disabled} onAction={onAction} card={card} />
      {safeLink(card.link) && card.type !== "confirm_cancel" ? <Link to={card.link} className="mt-2 inline-block text-sm font-semibold text-sky-700">Open appointment</Link> : null}
    </article>
  );
}

export default AssistantCard;
